import { Box, Container, Grid, Paper } from '@mui/material'
import { messageBus } from '@operations/AppProvider'
import { EquipmentPlanningBreadcrump } from '@operations/features/equipmentPlanning/components/molecules/EquipmentPlanningBreadcrump'
import { AmendHousekeepingPlanDialog } from '@operations/features/workInstructions/components/organisms/AmendHousekeepingPlanDialog'
import { CancelHousekeepingPlanDialog } from '@operations/features/workInstructions/components/organisms/CancelHousekeepingPlanDialog'
import { HousekeepingGroupActions } from '@operations/features/workInstructions/components/organisms/HousekeepingGroupActions'
import { NameWorkQueueDialogContainer } from '@operations/features/workInstructions/components/organisms/NameWorkQueueDialog'
import { WorkInstructionCarriers } from '@operations/features/workInstructions/components/organisms/WorkInstructionCarriers'
import { WorkInstructionCount } from '@operations/features/workInstructions/components/organisms/WorkInstructionCounts'
import { WorkInstructionHousekeepingGroups } from '@operations/features/workInstructions/components/organisms/WorkInstructionHousekeepingGroups'
import { WorkInstructionTable } from '@operations/features/workInstructions/components/organisms/WorkInstructionTable'
import { WorkInstructionTabs } from '@operations/features/workInstructions/components/organisms/WorkInstructionTabs'
import { WorkInstructionUIStore } from '@operations/features/workInstructions/stores/work-instruction.ui-store'
import { StartWorkQueueDialog } from '@operations/features/workQueues/components/molecules/StartWorkQueueDialog'
import { StopWorkQueueDialog } from '@operations/features/workQueues/components/molecules/StopWorkQueueDialog'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'

export const WorkInstructionsControlRoom = withAuthenticationRequired(
  observer(() => {
    const [searchParams] = useSearchParams()
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const vesselVisitIdString = searchParams.get('vesselVisitId')
    const vesselVisitId = vesselVisitIdString ? Number(vesselVisitIdString) : null

    const uiStore = useMemo(() => new WorkInstructionUIStore(messageBus), [])

    useAsyncFetch(async () => {
      await Promise.all([
        uiStore.fetchWorkInstructionTotalNumbers(),
        uiStore.fetchAndSetCarrierVisits(),
      ])
      uiStore.setFirstVisitIdForSelectedTab(vesselVisitId)
      if (uiStore.hasHousekeepingPanel) {
        await uiStore.fetchAndSetHousekeepingPlanSummaries()
      }
    })

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          minHeight: '100%',
        }}
      >
        <Helmet>
          <title>{t('WorkInstructionsTomOperation', 'Work Instructions - Tom Operation')}</title>
        </Helmet>
        {vesselVisitId ? <EquipmentPlanningBreadcrump visitId={vesselVisitId} /> : <></>}
        <Header title={t('controlRoomView', 'Control Room View')} />

        <Container sx={{ py: theme.customSpacing.m }}>
          <Paper elevation={4}>
            <WorkInstructionCount store={uiStore} />
            <WorkInstructionTabs store={uiStore} />

            <Grid container>
              {uiStore.hasSidePanel && (
                <Grid size={{ xs: 12, sm: 5, lg: 3 }}>
                  {uiStore.hasHousekeepingPanel ? (
                    <WorkInstructionHousekeepingGroups store={uiStore} />
                  ) : (
                    <WorkInstructionCarriers store={uiStore} />
                  )}
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  sm: uiStore.hasSidePanel ? 7 : 12,
                  lg: uiStore.hasSidePanel ? 9 : 12,
                }}
              >
                {uiStore.hasHousekeepingPanel && (
                  <HousekeepingGroupActions store={uiStore} />
                )}
                <WorkInstructionTable store={uiStore} />
              </Grid>
            </Grid>
          </Paper>
        </Container>

        <NameWorkQueueDialogContainer
          isOpen={uiStore.isNameWorkQueueDialogOpen}
          queue={uiStore.selectedWorkQueueForAction}
          handleClose={() => uiStore.setIsNameWorkQueueDialogOpen(false)}
          handleSubmit={(id, name) => uiStore.updateWorkQueueName(id, name)}
        />

        <StartWorkQueueDialog
          open={uiStore.isStartWorkQueueDialogOpen}
          workQueueId={uiStore.selectedWorkQueueForAction?.id}
          workQueueStatus={uiStore.selectedWorkQueueForAction?.status}
          onClose={() => uiStore.toggleStartWorkQueueDialog()}
          onConfirm={() => uiStore.toggleStartWorkQueueDialog()}
        />
        <StopWorkQueueDialog
          open={uiStore.isStopWorkQueueDialogOpen}
          workQueueId={uiStore.selectedWorkQueueForAction?.id}
          onClose={() => uiStore.toggleStopWorkQueueDialog()}
          onConfirm={() => uiStore.toggleStopWorkQueueDialog()}
        />

        <AmendHousekeepingPlanDialog store={uiStore} />
        <CancelHousekeepingPlanDialog store={uiStore} />
      </Box>
    )
  }),
)
